import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Button, Grid, Paper, Chip, Tooltip,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, FormControl, InputLabel, Select, MenuItem, Dialog,
  DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
  PersonAdd as AssignIcon, AutoFixHigh as AutoAssignIcon,
  FilterList as FilterIcon, FileDownload as ExportIcon,
  SwapHoriz as SwapIcon, DriveFileMove as MoveIcon,
  GroupWork as BulkIcon
} from "@mui/icons-material";
import PrintIcon from "@mui/icons-material/Print";
import { fetchData, postData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import AssignSectionDialog from "./AssignSectionDialog";
import { exportToCSV } from "../../utils/tableUtils";
import { printDocument, field, row, schoolHeader } from "../../utils/printUtils";

const GRADES = [
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8", "Seal of Bi-literacy"
];

const ACADEMIC_YEAR = "2026-2027";

// ── SwitchDialog ────────────────────────────────────────────────────────────
const SwitchDialog = ({ open, onClose, student, sections, onSuccess }) => {
  const [targetSection, setTargetSection] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSwap = async () => {
    if (!targetSection) { setError("Select a target section"); return; }
    setLoading(true); setError("");
    try {
      await putData("admin/admission/sections/switch", {
        applicationId: student?.id,
        targetSectionId: parseInt(targetSection),
        reason: reason || "Swap"
      });
      onSuccess(`Student swapped to ${sections.find(s => s.id === parseInt(targetSection))?.displayName}`);
      setTargetSection(""); setReason("");
    } catch (err) {
      setError(err?.message || "Swap failed");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={() => { onClose(); setTargetSection(""); setReason(""); setError(""); }} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>Swap Section</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {student?.firstName} {student?.lastName} — current: {student?.sectionName || "-"}
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Target Section *</InputLabel>
          <Select value={targetSection} label="Target Section *" onChange={(e) => setTargetSection(e.target.value)}>
            {sections.map(sec => (
              <MenuItem key={sec.id} value={sec.id}>
                {sec.displayName} ({sec.enrolledCount}/{sec.maxCapacity})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField fullWidth size="small" label="Reason (optional)" value={reason}
          onChange={(e) => setReason(e.target.value)} />
        {error && <Typography color="error" variant="body2" mt={1}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => { onClose(); setTargetSection(""); setReason(""); setError(""); }} variant="outlined" color="inherit">Cancel</Button>
        <Button onClick={handleSwap} variant="contained" disabled={loading}
          sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)" }}>
          {loading ? "Swapping..." : "Swap"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const AdminAdmissions = () => {
  const [activeTab, setActiveTab] = useState(0);          // 0=Unassigned, 1=Assigned, 2=Moved/Swapped
  const [selectedGrade, setSelectedGrade] = useState(0);
  const [students, setStudents] = useState([]);
  const [sections, setSections] = useState([]);
  const [movedStudents, setMovedStudents] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const grade = GRADES[selectedGrade];
  const isNilai8 = grade === "Nilai 8";

  const loadData = useCallback(async () => {
    try {
      const [studentsRes, sectionsRes] = await Promise.all([
        fetchData(`admin/admission/admitted?grade=${encodeURIComponent(grade)}&academicYear=${ACADEMIC_YEAR}`),
        fetchData(`admin/admission/sections?grade=${encodeURIComponent(grade)}&academicYear=${ACADEMIC_YEAR}`)
      ]);
      setStudents(studentsRes?.data || []);
      setSections(sectionsRes?.data || []);
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load data" });
    }

    try {
      const movedRes = await fetchData(`admin/admission/moved?grade=${encodeURIComponent(grade)}&academicYear=${ACADEMIC_YEAR}`);
      setMovedStudents(movedRes?.data || []);
    } catch (err) { /* silent – endpoint may not exist yet */ }
  }, [grade]);

  useEffect(() => { loadData(); }, [loadData]);

  const unassigned = students.filter(s => !s.isAssigned);
  const assigned = students.filter(s => s.isAssigned);

  // Apply text filter
  const applyFilter = (list) => {
    if (!filterText.trim()) return list;
    const q = filterText.toLowerCase();
    return list.filter(s =>
      [s.firstName, s.lastName, s.registrationNumber].some(v => v?.toLowerCase().includes(q))
    );
  };

  const handleAutoAssign = async () => {
    try {
      const result = await postData("admin/admission/sections/auto-assign", {
        grade, academicYear: ACADEMIC_YEAR
      });
      setToast({ open: true, severity: "success", message: result?.message || "Auto-assign completed" });
      loadData();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Auto-assign failed" });
    }
  };

  const handleBulkAssign = async () => {
    if (unassigned.length === 0) return;
    try {
      await postData("admin/admission/sections/auto-assign", {
        grade, academicYear: ACADEMIC_YEAR
      });
      setToast({ open: true, severity: "success", message: "Bulk assignment completed" });
      loadData();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Bulk assign failed" });
    }
  };

  const handleAssignSuccess = (msg) => {
    setToast({ open: true, severity: "success", message: msg });
    setAssignDialogOpen(false);
    loadData();
  };

  const handleSwitchSuccess = (msg) => {
    setToast({ open: true, severity: "success", message: msg });
    setSwitchDialogOpen(false);
    loadData();
  };

  const handleMoveStudent = (student) => {
    setSelectedStudent(student);
    setAssignDialogOpen(true);
  };

  const handleAdmissionLetter = (student) => {
    printDocument(`Admission Letter — ${student?.firstName}`, `
      ${schoolHeader('Admission Confirmation Letter', new Date().toLocaleDateString())}
      <div class="section">
        <p style="margin-bottom:16px">Dear <strong>${student?.parents?.find(p => p.parentType === 'parent1') ? `${student.parents.find(p => p.parentType === 'parent1').firstName} ${student.parents.find(p => p.parentType === 'parent1').lastName}` : 'Parent/Guardian'}</strong>,</p>
        <p style="margin-bottom:16px">We are pleased to inform you that <strong>${student?.firstName} ${student?.lastName}</strong> has been successfully admitted to Cumming Tamil School for the academic year 2026–2027.</p>
        ${row(field('Student Name', `${student?.firstName} ${student?.lastName}`), field('Grade', student?.grade || student?.gradeApplyingFor))}
        ${row(field('Section', student?.sectionName || '—'), field('Registration No', student?.registrationNumber || '—'))}
        <p style="margin-top:16px">Please keep this letter for your records. Further details regarding the academic schedule will be shared prior to the start of the academic year.</p>
        <p style="margin-top:24px">Warm regards,<br/><strong>Principal</strong><br/>Cumming Tamil School</p>
      </div>
    `);
  };

  const handleExport = () => {
    const cols = [
      { label: "App #", field: "applicationNumber" },
      { label: "Reg #", field: "registrationNumber" },
      { label: "First Name", field: "firstName" },
      { label: "Last Name", field: "lastName" },
      { label: "DOB", field: "dob" },
      { label: "Grade", field: "grade" },
      { label: "Section", field: "sectionName" }
    ];
    const data = activeTab === 0 ? unassigned : activeTab === 1 ? assigned : movedStudents;
    exportToCSV(cols, data, `admissions_${grade}_${activeTab === 0 ? "unassigned" : activeTab === 1 ? "assigned" : "moved"}.csv`);
  };

  // Boolean chip helper
  const BoolChip = ({ value }) => (
    <Chip label={value ? "Yes" : "No"} size="small" sx={{
      backgroundColor: value ? "#e8f5e9" : "#fce4ec",
      color: value ? "#2e7d32" : "#c62828",
      fontWeight: 600, fontSize: 11
    }} />
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Admissions</Typography>
      <Typography variant="h5" fontWeight="bold" mb={0.5}>Admissions Management</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>List of Students Enrolled</Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: "#e8f5e9" }}>
            <Typography variant="h4" fontWeight="bold" color="#2e7d32">{students.length}</Typography>
            <Typography variant="body2" color="text.secondary">Admitted</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: "#fff3e0" }}>
            <Typography variant="h4" fontWeight="bold" color="#e65100">{unassigned.length}</Typography>
            <Typography variant="body2" color="text.secondary">Unassigned</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: "#e3f2fd" }}>
            <Typography variant="h4" fontWeight="bold" color="#1565c0">{sections.length}</Typography>
            <Typography variant="body2" color="text.secondary">Sections</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Grade Tabs */}
      <Tabs value={selectedGrade} onChange={(_, v) => setSelectedGrade(v)}
        variant="scrollable" scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        {GRADES.map((g) => <Tab key={g} label={g} />)}
      </Tabs>

      {/* Grade group header with capacity badges */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1, alignSelf: "center" }}>{grade}:</Typography>
        {sections.map(sec => (
          <Chip key={sec.id} size="small"
            label={`${sec.displayName}: ${sec.enrolledCount}/${sec.maxCapacity}`}
            sx={{
              backgroundColor: sec.enrolledCount >= sec.maxCapacity ? "#fce4ec" : "#e8f5e9",
              color: sec.enrolledCount >= sec.maxCapacity ? "#c62828" : "#2e7d32",
              fontWeight: 600
            }} />
        ))}
      </Box>

      {/* Toolbar: filters + action buttons */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField size="small" placeholder="Search student..." value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ width: 200 }} />
        <Button variant="outlined" size="small" startIcon={<FilterIcon />}
          sx={{ textTransform: "none" }}>
          Filter
        </Button>
        <Button variant="outlined" size="small" startIcon={<ExportIcon />}
          onClick={handleExport} sx={{ textTransform: "none" }}>
          Export CSV
        </Button>
        {unassigned.length > 0 && (
          <>
            <Button variant="contained" size="small" startIcon={<AutoAssignIcon />}
              onClick={handleAutoAssign}
              sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", textTransform: "none" }}>
              Auto-Assign ({unassigned.length})
            </Button>
            <Button variant="contained" size="small" startIcon={<BulkIcon />}
              onClick={handleBulkAssign}
              sx={{ backgroundColor: "#ff8f00", "&:hover": { backgroundColor: "#e65100" }, textTransform: "none" }}>
              Bulk-Assign
            </Button>
          </>
        )}
      </Box>

      {/* Content Tabs: Unassigned / Assigned / Moved */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab label={`Unassigned List (${unassigned.length})`} />
        <Tab label={`Assigned List (${assigned.length})`} />
        <Tab label={`Moved / Swapped List (${movedStudents.length})`} />
      </Tabs>

      {/* ── Unassigned List ── */}
      {activeTab === 0 && (
        <Box mb={3}>
          {unassigned.length === 0 ? (
            <Typography variant="body2" color="text.secondary">All students are assigned.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>App #</TableCell>
                    <TableCell>Reg #</TableCell>
                    <TableCell>Student Name</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell>Parent Name</TableCell>
                    {isNilai8 && <TableCell>Registered for Nilai08</TableCell>}
                    {isNilai8 && <TableCell>Seal of Biliteracy</TableCell>}
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applyFilter(unassigned).map(s => {
                    const parent1 = s.parents?.find(p => p.parentType === "parent1");
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.applicationNumber || "-"}</TableCell>
                        <TableCell>{s.registrationNumber || "-"}</TableCell>
                        <TableCell>{s.firstName} {s.lastName}</TableCell>
                        <TableCell>{s.dob}</TableCell>
                        <TableCell>{parent1 ? `${parent1.firstName} ${parent1.lastName}` : "-"}</TableCell>
                        {isNilai8 && <TableCell><BoolChip value={s.registeredForNilai08} /></TableCell>}
                        {isNilai8 && <TableCell><BoolChip value={s.sealOfBiliteracy} /></TableCell>}
                        <TableCell>
                          <Button size="small" variant="outlined"
                            onClick={() => { setSelectedStudent(s); setAssignDialogOpen(true); }}
                            startIcon={<AssignIcon />} sx={{ textTransform: "none" }}>
                            Assign
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── Assigned List ── */}
      {activeTab === 1 && (
        <Box mb={3}>
          {/* Sections with group headers */}
          {sections.map(sec => {
            const secStudents = applyFilter(assigned.filter(s => s.sectionId === sec.id || s.sectionName === sec.displayName));
            return (
              <Box key={sec.id} mb={2}>
                {/* Grade group header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, px: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">{sec.displayName}</Typography>
                  <Chip size="small" label={`${sec.enrolledCount}/${sec.maxCapacity}`}
                    color={sec.enrolledCount >= sec.maxCapacity ? "error" : "success"} />
                </Box>
                <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 1 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableRow>
                        <TableCell>App #</TableCell>
                        <TableCell>Reg #</TableCell>
                        <TableCell>Student Name</TableCell>
                        <TableCell>Section</TableCell>
                        <TableCell>DOB</TableCell>
                        {isNilai8 && <TableCell>Registered for Nilai08</TableCell>}
                        {isNilai8 && <TableCell>Seal of Biliteracy</TableCell>}
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {secStudents.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isNilai8 ? 8 : 6}>
                            <Typography variant="body2" color="text.secondary">No students assigned to this section</Typography>
                          </TableCell>
                        </TableRow>
                      ) : secStudents.map(s => (
                        <TableRow key={s.id} hover>
                          <TableCell>{s.applicationNumber || "-"}</TableCell>
                          <TableCell>{s.registrationNumber || "-"}</TableCell>
                          <TableCell>{s.firstName} {s.lastName}</TableCell>
                          <TableCell>{s.sectionName}</TableCell>
                          <TableCell>{s.dob}</TableCell>
                          {isNilai8 && <TableCell><BoolChip value={s.registeredForNilai08} /></TableCell>}
                          {isNilai8 && <TableCell><BoolChip value={s.sealOfBiliteracy} /></TableCell>}
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Move to another section">
                                <IconButton size="small" onClick={() => handleMoveStudent(s)}>
                                  <MoveIcon fontSize="small" sx={{ color: "#244C77" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Swap with another student">
                                <IconButton size="small"
                                  onClick={() => { setSelectedStudent(s); setSwitchDialogOpen(true); }}>
                                  <SwapIcon fontSize="small" sx={{ color: "#ff9800" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Admission Letter">
                                <IconButton size="small" onClick={() => handleAdmissionLetter(s)}>
                                  <PrintIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
          {/* Any assigned students not yet in a section bucket */}
          {(() => {
            const sectionIds = new Set(sections.map(s => s.id));
            const orphans = applyFilter(assigned.filter(s => !sectionIds.has(s.sectionId)));
            if (orphans.length === 0) return null;
            return (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold" mb={0.5} px={1}>Other Assigned</Typography>
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableRow>
                        <TableCell>App #</TableCell>
                        <TableCell>Reg #</TableCell>
                        <TableCell>Student Name</TableCell>
                        <TableCell>Section</TableCell>
                        {isNilai8 && <TableCell>Registered for Nilai08</TableCell>}
                        {isNilai8 && <TableCell>Seal of Biliteracy</TableCell>}
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orphans.map(s => (
                        <TableRow key={s.id} hover>
                          <TableCell>{s.applicationNumber || "-"}</TableCell>
                          <TableCell>{s.registrationNumber || "-"}</TableCell>
                          <TableCell>{s.firstName} {s.lastName}</TableCell>
                          <TableCell>{s.sectionName || "-"}</TableCell>
                          {isNilai8 && <TableCell><BoolChip value={s.registeredForNilai08} /></TableCell>}
                          {isNilai8 && <TableCell><BoolChip value={s.sealOfBiliteracy} /></TableCell>}
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Move">
                                <IconButton size="small" onClick={() => handleMoveStudent(s)}>
                                  <MoveIcon fontSize="small" sx={{ color: "#244C77" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Swap">
                                <IconButton size="small"
                                  onClick={() => { setSelectedStudent(s); setSwitchDialogOpen(true); }}>
                                  <SwapIcon fontSize="small" sx={{ color: "#ff9800" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Admission Letter">
                                <IconButton size="small" onClick={() => handleAdmissionLetter(s)}>
                                  <PrintIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })()}
        </Box>
      )}

      {/* ── Moved / Swapped List ── */}
      {activeTab === 2 && (
        <Box mb={3}>
          {movedStudents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No moved or swapped students.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>App #</TableCell>
                    <TableCell>Reg #</TableCell>
                    <TableCell>Student Name</TableCell>
                    <TableCell>From Section</TableCell>
                    <TableCell>To Section</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applyFilter(movedStudents).map((s, idx) => (
                    <TableRow key={s.id || idx} hover>
                      <TableCell>{s.applicationNumber || "-"}</TableCell>
                      <TableCell>{s.registrationNumber || "-"}</TableCell>
                      <TableCell>{s.firstName} {s.lastName}</TableCell>
                      <TableCell>{s.fromSection || "-"}</TableCell>
                      <TableCell>{s.toSection || "-"}</TableCell>
                      <TableCell>{s.movedOn?.slice(0, 10) || s.updatedAt?.slice(0, 10) || "-"}</TableCell>
                      <TableCell>
                        <Chip label={s.reason || "Promoted"} size="small"
                          sx={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Dialogs */}
      {selectedStudent && (
        <>
          <AssignSectionDialog open={assignDialogOpen}
            onClose={() => setAssignDialogOpen(false)}
            student={selectedStudent} sections={sections}
            onSuccess={handleAssignSuccess} />
          <SwitchDialog open={switchDialogOpen}
            onClose={() => setSwitchDialogOpen(false)}
            student={selectedStudent} sections={sections}
            onSuccess={handleSwitchSuccess} />
        </>
      )}

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminAdmissions;
